import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TimeSelect } from "@/components/TimeSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDateIT, monthKey, monthLabel, fmtHours } from "@/lib/dates";

export default function AdminHours() {
  const [cursor, setCursor] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState("all");
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [wdate, setWdate] = useState("");
  const [note, setNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const month = monthKey(cursor);

  const load = async () => {
    const q = empFilter === "all" ? "" : `&employee_id=${empFilter}`;
    const [e, u] = await Promise.all([
      api.get(`/hours/admin?month=${month}${q}`),
      api.get("/users"),
    ]);
    setEntries(e.data);
    setEmployees(u.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, empFilter]);

  const move = (delta) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const openEdit = (e) => {
    setEditing(e);
    setStart(e.start_time);
    setEnd(e.end_time);
    setWdate(e.work_date);
    setNote(e.note || "");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/hours/${editing.id}`, { work_date: wdate, start_time: start, end_time: end, note });
      toast.success("Ore aggiornate");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/hours/${deleteTarget.id}`);
      toast.success("Registrazione eliminata");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-head text-2xl font-bold text-slate-900">Modifica ore</h1>

      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-2">
        <button onClick={() => move(-1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95"><ChevronLeft className="h-6 w-6" /></button>
        <span className="font-head font-bold text-lg">{monthLabel(cursor)}</span>
        <button onClick={() => move(1)} className="h-11 w-11 rounded-lg border border-slate-200 flex items-center justify-center active:scale-95"><ChevronRight className="h-6 w-6" /></button>
      </div>

      <Select value={empFilter} onValueChange={setEmpFilter}>
        <SelectTrigger data-testid="hours-emp-filter" className="h-12 rounded-xl">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti i dipendenti</SelectItem>
          {employees.map((e) => (
            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="space-y-2">
        {entries.length === 0 && <p className="text-slate-400 text-sm">Nessuna registrazione questo mese.</p>}
        {entries.map((e) => (
          <div key={e.id} data-testid="admin-hours-entry" className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{e.employee_name} · {e.job_name}</p>
                <p className="text-sm text-slate-500">{formatDateIT(e.work_date)} · {e.start_time}–{e.end_time} · <span className="font-semibold text-primary">{fmtHours(e.hours)} h</span></p>
              </div>
              <div className="flex gap-2 ml-2">
                <button data-testid={`edit-hours-${e.id}`} onClick={() => openEdit(e)} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 active:scale-95"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-hours-${e.id}`} onClick={() => setDeleteTarget(e)} className="h-10 w-10 rounded-lg border border-red-200 flex items-center justify-center text-destructive active:scale-95"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {e.note && (
              <p data-testid="admin-entry-note" className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg px-3 py-2">{e.note}</p>
            )}
          </div>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Correggi ore</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Data</label>
              <input data-testid="edit-work-date" type="date" value={wdate} onChange={(e) => setWdate(e.target.value)} className="w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Inizio</label>
                <TimeSelect value={start} onChange={setStart} testid="edit-start" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Fine</label>
                <TimeSelect value={end} onChange={setEnd} testid="edit-end" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Nota</label>
              <textarea data-testid="edit-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} placeholder="Cosa è stato fatto (facoltativo)" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base outline-none focus:border-primary resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-hours-button" onClick={save} disabled={busy} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva correzione"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa registrazione?</AlertDialogTitle>
            <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-hours" onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
